// SidebarBucket.jsx

// SidebarBucket.jsx

import React from 'react';

// CSS
import './SidebarBucket.css';

// Components
import SidebarBucketContent from './SidebarBucketContent';
import SidebarBucketHeader from './SidebarBucketHeader';

/**
 * SidebarBucket
 *
 * Renders a single expandable sidebar bucket with dashboard and database rows.
 *
 * @param {object} bucket - Bucket data with title, key, dashboards, and databases.
 * @param {boolean} isOpen - Whether this bucket is currently expanded.
 * @param {boolean} showHeader - Whether the bucket header should be rendered.
 * @param {function} onToggle - Called when the bucket header is clicked.
 * @param {function} onDashboardClick - Called when a dashboard row is clicked.
 * @param {function} onChannelClick - Called when a channel row is clicked.
 * @param {function} onDatabaseClick - Called when a database row is clicked.
 * @param {function} onAddFromTemplate - Called when the add dashboard action is clicked.
 * @param {function} onAddChannel - Called when the add channel action is clicked.
 * @param {function} onAddDatabase - Called when the add database action is clicked.
 *
 * @returns {ReactNode} The rendered sidebar bucket.
 */
const SidebarBucket = React.memo(({
    bucket,
    isOpen,
    showHeader = true,
    onToggle,
    onDashboardClick,
    onChannelClick,
    onDatabaseClick,
    onAddFromTemplate,
    onAddChannel,
    onAddDatabase,
}) => {
    const isBucketOpen = showHeader ? isOpen : true;

    // ----- RENDER ----- //

    return (
        <div className={`sidebar-bucket-container ${isBucketOpen ? 'is-open' : ''}`}>
            {showHeader && (
                <SidebarBucketHeader
                    bucketKey={bucket?.key}
                    title={bucket?.title}
                    isOpen={isBucketOpen}
                    onToggle={onToggle}
                />
            )}

            <SidebarBucketContent
                bucket={bucket}
                isOpen={isBucketOpen}
                showHeader={showHeader}
                onDashboardClick={onDashboardClick}
                onChannelClick={onChannelClick}
                onDatabaseClick={onDatabaseClick}
                onAddFromTemplate={onAddFromTemplate}
                onAddChannel={onAddChannel}
                onAddDatabase={onAddDatabase}
            />
        </div>
    );
});

// Dev Tools
SidebarBucket.displayName = 'SidebarBucket';

export default SidebarBucket;